import { useState, useEffect, useCallback } from "react"
import { GetSpamStats } from "./api"

/**
 * Custom hook for fetching and managing spam protection statistics
 * @param {number} refreshInterval - How often to refresh in milliseconds (default: 60000)
 * @returns {Object} Statistics data and control methods
 */
const useSpamStatistics = (refreshInterval = 60000) => {
    const [statistics, setStatistics] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [lastUpdated, setLastUpdated] = useState(null)

    // Function to fetch statistics
    const fetchStatistics = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)

            const response = await GetSpamStats()

            if (response.error) {
                setError(response.error)
                return
            }

            // Update statistics and timestamp
            setStatistics(response)
            setLastUpdated(new Date())
        } catch (err) {
            console.error("Error fetching spam statistics:", err)
            setError(err.message || "Failed to fetch statistics")
        } finally {
            setLoading(false)
        }
    }, [])

    // Function to manually refresh statistics
    const refresh = useCallback(() => {
        return fetchStatistics()
    }, [fetchStatistics])

    // Initial fetch on mount
    useEffect(() => {
        fetchStatistics()
    }, [fetchStatistics])

    // Set up automatic refresh interval
    useEffect(() => {
        // Skip if refresh interval is 0 or negative
        if (refreshInterval <= 0) return

        const intervalId = setInterval(() => {
            fetchStatistics()
        }, refreshInterval)

        // Clean up interval on unmount
        return () => clearInterval(intervalId)
    }, [fetchStatistics, refreshInterval])

    return {
        statistics,
        loading,
        error,
        lastUpdated,
        refresh
    }
}

export default useSpamStatistics